import React from 'react';
import { Box, Chip } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { PatientProfileInfoAccordion, PatientProfileInfoAccordionEditable } from '../PatientProfileInfoAccordion';
import { Add, PersonOutline, PhoneInTalkOutlined } from '@mui/icons-material';
import { EmergencyContact, PutUserEmergencyContactErrorResponse } from '@uplift/api';
import { ContactInfoRow, EmergencyContactForm, useEmergencyContactForm } from '../../../../../molecules';
import { Button } from './../../../../../Button';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useL10n } from '@uplift/hooks';

const title = 'Emergency Contact';

type CanAddEmergencyContact = {
  canAdd?: boolean;
  inlineForm?: boolean;
  patientId: string;
};
type CannotAddEmergencyContact = {
  canAdd?: never;
  inlineForm?: never;
  patientId?: never;
};

export type EmergencyContactAccordionProps = {
  emergencyContact?: Pick<EmergencyContact, 'relationship' | 'name' | 'phone'>;
} & (CanAddEmergencyContact | CannotAddEmergencyContact);


export const EmergencyContactAccordion: React.VFC<EmergencyContactAccordionProps> = (props) => {

  if (!props.emergencyContact && props.canAdd) {
    return <EmergencyContactAccordianEditable {...props} />;
  }

  if (props.emergencyContact === undefined) {
    return (
      <PatientProfileInfoAccordion
        title={title}
        chip={(
          <Chip size="small" label="None" />
        )}
      />
    );
  }

  if (!props.emergencyContact) {
    return (
      <PatientProfileInfoAccordion
        title={title}
        compensateExpandableIcon
        chip={(
          <WarningChip size="small" label="Ask client to complete" />
        )}
      />
    );
  }

  return (
    <PatientProfileInfoAccordion
      title={title}
      chip={(
        <Chip size="small" label={props.emergencyContact.relationship} />
      )}
    >
      <Box display="flex" flexDirection="column" gap="8px" width="100%">
        <ContactInfoRow icon={PersonOutline} primary={props.emergencyContact.name} />
        <ContactInfoRow icon={PhoneInTalkOutlined} primary={props.emergencyContact.phone} secondary="home" text={props.emergencyContact.phone} type="phone" />
      </Box>
    </PatientProfileInfoAccordion>
  );
};

export const WarningChip = withStyles(Chip, theme => ({
  root: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.common.white,
  }
}));

type EmergencyContactAccordianEditableProps =
    & Pick<EmergencyContactAccordionProps, 'emergencyContact'>
    & Pick<CanAddEmergencyContact, 'patientId' | 'inlineForm'>;

const EmergencyContactAccordianEditable: React.VFC<EmergencyContactAccordianEditableProps> = (props) => {
  const l10n = useL10n();
  const queryClient = useQueryClient();
  const { emergencyContactFormik } = useEmergencyContactForm(props.patientId, props.emergencyContact);

  return (
    <PatientProfileInfoAccordionEditable
      title={title}
      editable
      hideEditIcon
      inlineForm={props.inlineForm}
      formikConfig={emergencyContactFormik()}
      form={(
        <EmergencyContactForm
          labels={{
            name: 'Name',
            relationship: 'Relationship',
            phone: 'Phone Number'
          }}
        />
      )}
      onSuccess={async (alert, onCancelEdit) => {
        alert.success('Emergency Contact Updated');
        await queryClient.invalidateQueries({ queryKey: [ 'user', props.patientId, 'emergencyContact' ] });
        onCancelEdit();
      }}
      onError={(error: AxiosError<PutUserEmergencyContactErrorResponse>, alert) => {
        if (error?.response?.data?.fields?.phone) {
          alert.error('Enter a valid phone number for emergency contact');
        } else {
          alert.error(`There was a problem updating this emergency contact. Try again or contact ${l10n.helpEmail}`);
        }
      }}
    >
      {(onEdit) => {
        return (
          <Box display="flex" justifyContent="center" mb={1} width="100%">
            <Button
              onClick={onEdit}
              color="secondary"
              variant="outlined"
              startIcon={<Add />}
            >
                Add Emergency Contact
            </Button>
          </Box>
        );
      }}
    </PatientProfileInfoAccordionEditable>
  );
};
